import { ExternalLink, Play } from "lucide-react";
import { site } from "@/content/site";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";

export default function Video() {
  const { featured, gallery } = site.videos;

  return (
    <>
      <SEO
        title="Video | EVE Band — Live Performances & Music Videos"
        description="Feel the energy — watch EVE's official music videos and live performances, including Pretty — Live Performance, Destiny, Mama, Pulang Kembali and Running Through the Dark."
      />
      <section data-testid="video-page" className="pb-24 pt-32 lg:pb-32 lg:pt-40">
        <div className="container-eve">
          <Reveal>
            <p className="eyebrow mb-6">Video</p>
            <h1 className="font-display text-5xl uppercase leading-[0.95] sm:text-6xl lg:text-7xl">
              Feel the <span className="gradient-text">energy</span>
            </h1>
            <p className="mt-6 text-base text-eve-silver sm:text-lg">
              Experience the sound. Witness the energy.
            </p>
          </Reveal>

          {/* Featured video */}
          <Reveal delay={0.1}>
            <div data-testid="featured-video-block" className="mt-16">
              <div className="relative aspect-video w-full overflow-hidden border border-white/10 bg-eve-surface">
                {featured.youtubeId ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${featured.youtubeId}`}
                    title={featured.title}
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 h-full w-full"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-[radial-gradient(circle_at_50%_40%,rgba(123,44,255,0.16),transparent_65%)] px-6 text-center">
                    <span className="flex h-20 w-20 items-center justify-center rounded-full border border-white/20 text-white/70">
                      <Play size={26} className="ml-1" />
                    </span>
                    <div>
                      <p className="font-display text-3xl uppercase tracking-wide sm:text-4xl">
                        {featured.title}
                      </p>
                      <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-eve-silver/80">
                        Official video pending — the embed appears automatically once the YouTube
                        link is added to the video settings
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-eve-silver">
                  Featured — {featured.title}
                </p>
                {featured.youtubeUrl ? (
                  <a
                    href={featured.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="watch-on-youtube-btn"
                    className="btn-primary"
                  >
                    Watch on YouTube <ExternalLink size={14} />
                  </a>
                ) : (
                  <span
                    data-testid="youtube-link-pending"
                    className="chip text-white/40"
                    title="Add the official YouTube URL in content settings to activate this button"
                  >
                    YouTube link pending
                  </span>
                )}
              </div>
            </div>
          </Reveal>

          {/* Video gallery — renders only with real, verified entries */}
          <Reveal delay={0.05} className="mt-24">
            <h2 className="font-display text-3xl uppercase sm:text-4xl">
              Video gallery<span className="text-eve-cyan">.</span>
            </h2>
          </Reveal>
          {gallery.length > 0 ? (
            <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {gallery.map((v) => (
                <a
                  key={v.id}
                  href={v.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`video-gallery-${v.id}`}
                  className="group relative aspect-video overflow-hidden border border-white/10 bg-eve-surface"
                >
                  <img
                    src={`https://i.ytimg.com/vi/${v.youtubeId}/hqdefault.jpg`}
                    alt={v.title}
                    loading="lazy"
                    className="h-full w-full object-cover opacity-80 transition-opacity duration-500 group-hover:opacity-100"
                  />
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-eve-bg to-transparent p-4 font-display text-lg uppercase tracking-wide">
                    {v.title}
                  </span>
                </a>
              ))}
            </div>
          ) : (
            <p
              data-testid="video-gallery-empty"
              className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-eve-silver/70"
            >
              More official videos coming soon.
            </p>
          )}
        </div>
      </section>
    </>
  );
}
